import { Component, forceUpdate, h, Prop, State, Watch } from "@stencil/core";
import { IChatData, IError, IMessage, IMiddlewareParams, sendMessage } from "../../backend/chatService";
import { i18nProvider } from "@trumpf/ux.i18n";
import loadTranslations, { TranslationKey, TranslationOptions } from "../../utils/loadTranslations";
import { debug, parseLinks, scrollElementToBottom } from "../../utils/utils";

/**
 * Ensures a valid ISO date string format for chat messages
 * If the input is a valid date string, returns it as is
 * Otherwise, returns a new ISO date string
 */
const ensureValidDateString = (
    dateStr: string | undefined,
    component?: { debug?: boolean; constructor?: { name?: string } }
): string => {
    if (!dateStr) {
        console.warn("[AiChat] No date string provided, using current date");
        return new Date().toISOString();
    }

    try {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
            if (component) {
                debug.log(component, "Successfully parsed date:", {
                    input: dateStr,
                    parsed: date.toISOString()
                });
            }
            return date.toISOString();
        }
        console.warn("[AiChat] Invalid date (NaN):", {
            input: dateStr,
            parsed: date
        });
    } catch (error) {
        console.error("[AiChat] Failed to parse date:", {
            input: dateStr,
            error: error.message
        });
    }
    return new Date().toISOString();
};

/**
 * Determines the appropriate date divider text for a message timestamp
 * Returns 'Today', 'Yesterday', or a formatted date for older messages
 */
const getDateDividerText = (dateStr: string, t: (key: TranslationKey, options?: TranslationOptions) => string): string => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const messageDate = new Date(dateStr);
    messageDate.setHours(0, 0, 0, 0);

    if (messageDate.getTime() === today.getTime()) {
        return t("dateDivider.today");
    } else if (messageDate.getTime() === yesterday.getTime()) {
        return t("dateDivider.yesterday");
    } else {
        return messageDate.toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric"
        });
    }
};

/**
 * Check if a date divider should be inserted before this message
 * Returns true if this is the first message or if the date changed from the previous message
 */
const shouldAddDateDivider = (currentMsg: IMessage, previousMsg: IMessage | undefined): boolean => {
    if (!previousMsg) return true;

    const currentDate = new Date(currentMsg.timestamp);
    const previousDate = new Date(previousMsg.timestamp);

    return currentDate.toDateString() !== previousDate.toDateString();
};

@Component({
    tag: "ai-chat",
    styleUrl: "ai-chat.scss",
    shadow: true
})
export class AiChat {
    /** Authentication token for API requests */
    @Prop() readonly token: string;

    /** Chat data containing messages and session information */
    @Prop({ mutable: true }) data: IChatData;

    /** The unique identifier of the case */
    @Prop() readonly caseId: string;

    /** Current status of the case (e.g., 'open', 'taked', 'in_progress') */
    @Prop() readonly caseStatus: string;

    /** Error state passed from wrapper */
    @Prop() readonly error: IError | null = null;

    /** Enable debug logging */
    @Prop() readonly debug = false;

    /** Machine name (e.g. "TruLaser1030 (L26)") */
    @Prop() readonly machineName?: string;

    /** Machine number identifier */
    @Prop() readonly machineNumber?: string;

    /** Software type (e.g. "TruTops Boost") */
    @Prop() readonly softwareType?: string;

    /** Software module (e.g. "HomeZone") */
    @Prop() readonly softwareModule?: string;

    /** Error description - free text field describing customer problem */
    @Prop() readonly errorDescription?: string;

    /** Error number(s) linked to the case during creation */
    @Prop() readonly errorNumbers?: string;

    /** Whether the chat is in service app mode */
    @Prop() readonly isServiceApp: boolean = false;

    @State() messageText = "";
    @State() chatMessages: IMessage[] = [];
    @State() isAiTyping = false;
    @State() translationsLoaded = false;

    private i18nSubscriberId: string;
    private chatRef?: HTMLElement & {
        showError: (config: {
            title?: string;
            message: string;
            icon?: string;
            showCloseButton?: boolean;
            closeButtonText?: string;
        }) => void;
        hideError: () => void;
    };
    private chatObserver: MutationObserver | null = null;

    /** Current locale for translations */
    @Prop({ reflect: true, mutable: true }) locale: string;

    @Watch("error")
    handleErrorChange(newError: IError | null, oldError: IError | null) {
        // Skip if error hasn't changed meaningfully
        if (newError === oldError) {
            return;
        }

        debug.log(this, "Error prop changed:", { newError, oldError });

        if (newError) {
            // Define the translation function locally or ensure it's accessible
            const t = (key: TranslationKey, options: TranslationOptions = {}) =>
                i18nProvider.t(`ai-in-service:${String(key)}`, {
                    ...options,
                    interpolation: { escapeValue: false },
                    lng: this.locale // Ensure correct locale is used
                });

            // Translate the message using the key and params
            const translatedMessage = t(newError.errorMessage as TranslationKey, (newError.params || {}) as TranslationOptions);

            debug.log(this, "Showing error via ux-chat:", {
                title: newError.errorCode ? `Error ${newError.errorCode}` : "Error",
                message: translatedMessage, // Use translated message
                buttonText: newError.buttonText,
                showIcon: newError.showIcon
            });

            if (this.chatRef) {
                this.chatRef.showError({
                    title: newError.showIcon !== false ? (newError.errorCode ? `Error ${newError.errorCode}` : "Error") : "",
                    message: translatedMessage, // Use translated message
                    icon: newError.showIcon === false ? undefined : "cross",
                    showCloseButton: !!newError.buttonText,
                    closeButtonText: newError.buttonText
                });
            } else {
                console.warn("[AiChat] Cannot show error - chatRef not set");
            }
        } else if (oldError) {
            debug.log(this, "Hiding error");
            if (this.chatRef) {
                this.chatRef.hideError();
            } else {
                console.warn("[AiChat] Cannot hide error - chatRef not set");
            }
        }
    }

    /**
     * Scrolls the chat container to the bottom
     */
    private scrollChatToBottom = () => {
        scrollElementToBottom(this.chatRef);
    };

    componentDidLoad() {
        debug.log(this, "Component loaded, chatRef exists:", !!this.chatRef);
        if (this.error) {
            this.handleErrorChange(this.error, null);
        }

        if (this.chatRef && !this.chatObserver) {
            this.chatObserver = new MutationObserver(() => {
                debug.log(this, "DOM mutation detected, ensuring scroll position");
                this.scrollChatToBottom();
            });

            this.chatObserver.observe(this.chatRef, {
                childList: true,
                subtree: true
            });
        }

        this.scrollChatToBottom();
    }

    componentDidUpdate() {
        this.scrollChatToBottom();
    }

    @Watch("data")
    handleDataChange() {
        this.scrollChatToBottom();
    }

    private handleKeyPress = (event: KeyboardEvent) => {
        if (event.key === "Enter" && !event.shiftKey && (event.target as HTMLInputElement).value) {
            const value = (event.target as HTMLInputElement).value;

            if (value.trim()) {
                const customEvent = new CustomEvent("uxMessageSend", {
                    detail: value.trim()
                });
                void this.handleMessageSend(customEvent);
            }
        }
    };

    private getMiddlewareParams(): IMiddlewareParams {
        const middlewareParams: IMiddlewareParams = {
            machineName: this.machineName,
            machineNumber: this.machineNumber,
            softwareType: this.softwareType,
            softwareModule: this.softwareModule,
            errorDescription: this.errorDescription,
            errorNumbers: this.errorNumbers
        };

        // Only include non-undefined properties
        return Object.fromEntries(Object.entries(middlewareParams).filter(([, value]) => value !== undefined)) as IMiddlewareParams;
    }

    private async handleMessageSend(event: CustomEvent<string>) {
        const messageText = event.detail;

        const userMessage: IMessage = {
            source: "user",
            message: messageText,
            timestamp: new Date().toISOString()
        };

        debug.log(this, "Sending message to server:", userMessage);

        this.isAiTyping = true;

        try {
            this.data = {
                ...this.data,
                messages: [...this.data.messages, userMessage]
            };

            // Get middleware params to pass with the request
            const middlewareParams = this.getMiddlewareParams();
            debug.log(this, "Middleware params for message:", middlewareParams);

            // Update sendMessage call to include middlewareParams when it's implemented in the backend
            const aiResponse = await sendMessage(this.caseId, this.data.sessionId, userMessage, middlewareParams);
            debug.log(this, "Received response from server:", aiResponse);

            const validatedResponse = {
                ...aiResponse,
                timestamp: ensureValidDateString(aiResponse.timestamp, this)
            };

            this.data = {
                ...this.data,
                messages: [...this.data.messages, validatedResponse]
            };

            this.isAiTyping = false;
            this.handleErrorChange(null, this.error);
        } catch (error) {
            console.error("Error getting AI response:", error);
            this.isAiTyping = false;
            this.handleErrorChange(error, this.error);
        }
    }

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => {
            this.translationsLoaded = true;
            forceUpdate(this);
        });

        document.addEventListener("caseStatusUpdated", this.handleCaseStatusUpdated.bind(this) as unknown as EventListener);
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
        document.removeEventListener("caseStatusUpdated", this.handleCaseStatusUpdated.bind(this) as unknown as EventListener);

        if (this.chatObserver) {
            this.chatObserver.disconnect();
            this.chatObserver = null;
        }
    }

    async componentWillLoad() {
        const { language } = i18nProvider;
        this.locale = language;
        await this.loadTranslations();
    }

    @Watch("locale")
    async onLocaleChange() {
        const currentLanguage = i18nProvider.language;
        const currentBase = currentLanguage.split("-")[0];
        const newBase = this.locale.split("-")[0];

        if (currentBase !== newBase) {
            await this.loadTranslations();
        }
    }

    private async loadTranslations() {
        try {
            const locale = await loadTranslations(this.locale);
            await i18nProvider.changeLanguage(locale);
        } catch (error) {
            console.error("Failed to load or change translations:", error);
        }
    }

    private renderSystemMessage = (message: IMessage) => {
        if (message.source === "system" || message.source === "bot") {
            const parsedMessage = parseLinks(message.message);
            return <span innerHTML={parsedMessage}></span>;
        }
        return message.message;
    };

    private handleCaseStatusUpdated(event: CustomEvent<string>) {
        debug.log(this, `Case status changed to "${event.detail}"`);
    }

    render() {
        const t = (key: TranslationKey, options: TranslationOptions = { returnObjects: true }) =>
            i18nProvider.t(`ai-in-service:${String(key)}`, {
                ...options,
                interpolation: { escapeValue: false }
            });

        if (!this.translationsLoaded) {
            return <div class="loading-state">{t("loading.translations")}</div>;
        }

        const validatedMessages =
            this.data.messages?.map((msg) => ({
                ...msg,
                timestamp: ensureValidDateString(msg.timestamp, this)
            })) || [];

        const allMessages = [...this.chatMessages, ...validatedMessages];

        return (
            <div class="ai-chat">
                <ux-chat
                    ref={(el) => (this.chatRef = el)}
                    typing={this.isAiTyping ? `${t("name.aiAssistant")} ${t("message.isTyping")}` : undefined}
                    is-service-app={this.isServiceApp}
                    style={{ minHeight: allMessages.length === 0 ? "450px" : undefined }}
                >
                    {allMessages.map((msg, index) => {
                        const previousMsg = index > 0 ? allMessages[index - 1] : undefined;
                        const addDateDivider = shouldAddDateDivider(msg, previousMsg);

                        return (
                            <div key={index}>
                                {addDateDivider && <ux-chat-date-divider text={getDateDividerText(msg.timestamp, t)} />}
                                <ux-chat-item
                                    key={index}
                                    source={msg.source as "system" | "bot" | "user"}
                                    name={msg.source === "bot" || msg.source === "system" ? t("name.aiAssistant") : t("name.user")}
                                    date={msg.timestamp}
                                >
                                    <ux-copy>{this.renderSystemMessage(msg)}</ux-copy>
                                </ux-chat-item>
                            </div>
                        );
                    })}

                    {this.caseStatus === "taken" && (
                        <ux-chat-item key={"feedback-message"} source={"system"}>
                            <ux-copy>
                                <span>{t("systemMessage.caseTaken")}</span>
                            </ux-copy>
                            <ux-button
                                href="https://cxtrumpf.qualtrics.com/jfe/form/SV_di1esknueE4gnbM"
                                id={`feedback-button`}
                                icon="dialog-thumbs"
                                icon-placement="end"
                            >
                                {t("systemMessage.feedbackButtonLabel")}
                            </ux-button>
                        </ux-chat-item>
                    )}
                    <ux-chat-message-bar
                        className={this.caseStatus === "taken" ? "chat-message-bar-disabled" : ""}
                        placeholder={t("messageBar.placeholder")}
                        slot="bar"
                        onUxMessageSend={(event) => this.handleMessageSend(event)}
                        onKeyPress={this.handleKeyPress}
                        disabled={this.caseStatus === "taken" ? true : false}
                    ></ux-chat-message-bar>
                </ux-chat>
            </div>
        );
    }
}
